import { FC, useMemo, useState, useEffect } from 'react';
import {
  KebabOption,
  OrganisationToggleEnum,
  UserManagementProps,
} from './userManagement.type';
import './userManagement.css';
import {
  addMenuOptionsforUserManagement,
  getUserKebabOptions,
  getLoadingActionMenu,
  getUserTableHeader,
  getUsersByOrganizationUnit,
  AddMenuOption,
} from './userManagementHelper';
import Table from '../../component/table/table';
import TableFooter from '../../component/table/tableFooter';
import {
  GetUsersResponse,
  OrganizationUnitNameAndId,
  UpdateUserResponse,
  UserStatus,
} from '@inplay/models';
import {
  useActivateDeactivate,
  useDeleteUser,
  useUsersSummary,
} from '../../hooks/useUserData';
import { UserModal } from '../../component/userModal';
import { UserOperation } from '../../component/userModal/type';
import SuccessToast from '../../component/successModal/successModal';
import { VenuesView } from './venuesView/venuesView';
import ConfirmationModal from '../../component/confirmationModal/confirmationModal';
import { Trans } from 'react-i18next';
import EditVenueBranch from './editOrgUnit';
import {
  apiStatusCode,
  StatusResponse,
  StatusText,
  SuccessMessage,
} from '../../utils/helper.type';
import { OrganizationUnitChildItem } from '../../component/organizationUnitDropdown/organizationUnitDropdown.type';
import { toast } from 'react-toastify';
import { UserManagementTableHeader } from './userManagementTableHeader';
import EditUniversalStaffLoginModal from '../../component/universalStaffLoginModal/EditUniversalStaffLoginModal';
import ViewUniversalStaffLoginModal from '../../component/universalStaffLoginModal/ViewUniversalStaffLogin';
import {
  BaseUserFilter,
  UserManagementFilter,
} from '../../component/userManagementFilter';
import { getFilteredUsers } from '../../component/userManagementFilter/helper';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  VenuesViewSearchParams,
  VenuesViewAddOptions,
} from './venuesView/venuesView.type';
import AddOrganisationModal from './venuesView/addOrganisationModal';

const UserManagement: FC<UserManagementProps> = () => {
  const location = useLocation();
  const [activeLabel, setActiveLabel] = useState<boolean>(false);
  const [showInvitedModal, setShowInvitedModal] = useState<boolean>(false);
  const [showChangePasswordSuccessToast, setShowChangePasswordSuccessToast] =
    useState<boolean>(false);
  const [showVenueBranchCreatedToast, setShowVenueBranchCreatedToast] =
    useState<boolean>(false);
  const [currentActivateAction, setCurrentActivateAction] = useState<
    KebabOption.Activate | KebabOption.InActivate | KebabOption.Delete | null
  >(null);
  const [bulkActionLoading, setBulkActionLoading] = useState<boolean>(false);
  const [userOperation, setUserOperation] = useState<UserOperation>(
    UserOperation.VIEW,
  );
  const [selectedChildOrganizationLabel, setSelectedChildOrgnizationLabel] =
    useState<OrganizationUnitChildItem>();

  const [openEditVenueModal, setOpenEditVenueModal] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState(BaseUserFilter);

  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  const [selectedUsers, setSelectedUsers] = useState<GetUsersResponse[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [selectedOrganisationToggle, setSelectedOrganisationToggle] =
    useState<OrganisationToggleEnum>(OrganisationToggleEnum.VENUE_USERS);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(
    null,
  );
  const [searchParams] = useSearchParams();

  const [showStatusConfirmationModal, setShowStatusConfirmationModal] =
    useState<boolean>(false);

  const [userDetailSummary, setUserDetailSummary] =
    useState<GetUsersResponse>();

  const [
    showActivateDeActivateSuccessToast,
    setShowActivateDeActivateSuccessToast,
  ] = useState<boolean>(false);
  const [showDeleteSuccessToast, setShowSuccessDelete] =
    useState<boolean>(false);

  // Universal Staff Login Modal
  const [showUniversalStaffLoginModal, setShowUniversalStaffLoginModal] =
    useState<boolean>(false);
  const [
    showEditUniversalStaffLoginModal,
    setShowEditUniversalStaffLoginModal,
  ] = useState<boolean>(false);

  const [addOrg, setAddOrg] = useState<{
    type: VenuesViewAddOptions | undefined;
    show: boolean;
  }>({
    type: undefined,
    show: false,
  });

  const confirmationModalText = useMemo(() => {
    if (currentActivateAction === KebabOption.Activate) {
      return StatusText.ACTIVATE;
    } else if (currentActivateAction === KebabOption.Delete) {
      return StatusText.DELETE;
    } else {
      return StatusText.DEACTIVATE;
    }
  }, [currentActivateAction]);

  const { deleteUser, deleteUserLoading } = useDeleteUser();

  const { activateDeactivateUser, activateDeactivateLoading } =
    useActivateDeactivate();

  const {
    usersSummary,
    userSummaryLoading,
    updateUserLocally,
    deleteUserLocally,
  } = useUsersSummary(true);

  useEffect(() => {
    if (location.state?.showVenueBranchCreatedToast) {
      setShowVenueBranchCreatedToast(true);
      // Clear the state after showing toast
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleUserSearchChange = (value: string) => {
    setUserSearchQuery(value);
  };

  const handleSelectionChange = (selectedUsers: GetUsersResponse[]) => {
    setSelectedUsers(selectedUsers);
  };

  const actionVisibility = useMemo(() => {
    const hasActive: boolean = selectedUsers.some(
      (user: GetUsersResponse) => user.status === UserStatus.ACTIVE,
    );
    const hasDeactivate: boolean = selectedUsers.some(
      (user: GetUsersResponse) => user.status === UserStatus.INACTIVE,
    );

    const hasSelectedUsers: boolean = selectedUsers.length > 0;

    return {
      isShowActivate: hasActive,
      isShowDeactivate: hasDeactivate,
      isShowDelete: hasSelectedUsers,
      isShowAdd: !hasSelectedUsers,
    };
  }, [selectedUsers]);

  const handleClose = () => {
    setSelectedUsers([]);
    return {
      isShowActivate: false,
      isShowDeactivate: false,
    };
  };

  const filteredUsers = useMemo(() => {
    let users: GetUsersResponse[] = usersSummary?.data ?? [];
    let showUsers: boolean =
      searchParams.get(VenuesViewSearchParams.SHOW_USERS) === 'true';
    let selectedOrgIdFromURL: string | undefined = searchParams
      .get(VenuesViewSearchParams.SELECTED_ORG_UNIT_IDS)
      ?.split(',')[0];

    if (showUsers && selectedOrgIdFromURL) {
      users = getUsersByOrganizationUnit(users, selectedOrgIdFromURL);
    } else if (selectedChildOrganizationLabel) {
      users = getUsersByOrganizationUnit(
        users,
        selectedChildOrganizationLabel.value,
      );
    }

    users = getFilteredUsers(users, filters);

    if (!userSearchQuery) return users;

    const query: string = userSearchQuery.toLowerCase();

    return users.filter((user: GetUsersResponse) => {
      const fullName: string = `${user.firstName ?? ''} ${
        user.lastName ?? ''
      }`.toLowerCase();
      const email: string = user.email?.toLowerCase() ?? '';
      const mobile: string = String(user.mobileNumber ?? '');
      const venueBranchNames: string = (user.venueBranchs ?? [])
        .map(
          (branch: OrganizationUnitNameAndId) =>
            branch.name?.toLowerCase() ?? '',
        )
        .join(' ');

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        mobile.includes(query) ||
        venueBranchNames.includes(query)
      );
    });
  }, [
    usersSummary?.data,
    userSearchQuery,
    selectedChildOrganizationLabel,
    filters,
    searchParams,
  ]);

  const handleInviteUserStatus = (
    selectedUsers: GetUsersResponse[],
    setShowInvitedModal: (value: boolean) => void,
  ): boolean => {
    const invitedSelected: boolean = selectedUsers.some(
      (user: GetUsersResponse) => user.status === UserStatus.INVITED,
    );

    if (invitedSelected) {
      setShowInvitedModal(true);
      return true;
    }

    return false;
  };

  const handleBulkActivateDeactivate = async (setToActive: boolean) => {
    if (activateDeactivateLoading) {
      console.log(
        '[ActivateDeActivateUser] Already in progress... skipping this request',
      );
      return;
    }
    const selectedUserIds: string[] = selectedUsers.map(
      (user: GetUsersResponse) => user.id,
    );
    console.log(`[SELECTED USER IDS...]`, selectedUserIds);

    try {
      setBulkActionLoading(true);
      console.log(`[ENTRY][ActivateDeActivateUser]`);
      const activateDeActivateRowsResponse: UpdateUserResponse | undefined =
        await activateDeactivateUser(selectedUserIds, setToActive);

      const activateDeActivateUserResponse: StatusResponse | undefined =
        activateDeActivateRowsResponse
          ? {
              statusCode: apiStatusCode.SUCCESS,
              data: activateDeActivateRowsResponse,
            }
          : undefined;
      console.log(
        `[ActivateDeActivateUser] response `,
        activateDeActivateUserResponse,
      );
      if (
        activateDeActivateUserResponse?.statusCode === apiStatusCode.SUCCESS
      ) {
        updateUserLocally(selectedUserIds, setToActive);
        setSelectedUsers([]);
        setShowActivateDeActivateSuccessToast(true);
        console.log(`[ActivateDeActivateUser] close `);
      }
    } catch (error: unknown) {
      console.warn(`[ActivateDeActivateUser]`, { error });
      throw error;
    } finally {
      console.warn(`[ActivateDeActivateUser] finally block...`);
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (deleteUserLoading) {
      console.log('[DeleteUser] Already in progress... skipping this request');
      return;
    }
    const selectedUserIds: string[] = selectedUsers.map(
      (user: GetUsersResponse) => user?.id,
    );
    console.log(`[SELECTED USER IDS...]`, selectedUserIds);
    try {
      setBulkActionLoading(true);
      const deleteUserRowResponse: UpdateUserResponse | undefined =
        await deleteUser(selectedUserIds);

      const deleteUserResponse: StatusResponse | undefined =
        deleteUserRowResponse
          ? {
              statusCode: apiStatusCode.SUCCESS,
              data: deleteUserRowResponse,
            }
          : undefined;

      if (deleteUserResponse?.statusCode === apiStatusCode.SUCCESS) {
        deleteUserLocally(selectedUserIds);
        setSelectedUsers([]);
        setShowSuccessDelete(true);
      }
      console.log(
        `[DeleteUser][Close loading and receive success toast message].......`,
      );
    } catch (error: unknown) {
      console.warn(`[BulkDelete]`, { error });
      throw error;
    } finally {
      console.warn(`[BulkDelete] finally block...`);
      setBulkActionLoading(false);
    }
  };

  const handleActivate = () => {
    setCurrentActivateAction(KebabOption.Activate);
    if (handleInviteUserStatus(selectedUsers, setShowInvitedModal)) {
      return;
    }
    setShowStatusConfirmationModal(true);
  };

  const handleDeactivate = () => {
    setCurrentActivateAction(KebabOption.InActivate);
    if (handleInviteUserStatus(selectedUsers, setShowInvitedModal)) {
      return;
    }
    setShowStatusConfirmationModal(true);
  };

  const handleDelete = () => {
    setCurrentActivateAction(KebabOption.Delete);
    setShowStatusConfirmationModal(true);
  };

  const handleViewUser = (user: GetUsersResponse, index: number) => {
    setActiveRowIndex(index);
    setUserDetailSummary(user);
    setUserOperation(UserOperation.VIEW);
    setSelectedUserIndex(index);
    setShowUserModal(true);
  };

  const handleAddUser = (action: AddMenuOption) => {
    if (action === AddMenuOption.ADD_USER) {
      setShowUserModal(true);
      setUserOperation(UserOperation.ADD);
    } else if (action === AddMenuOption.ADD_VENUE_BRANCH) {
      setAddOrg({
        type: VenuesViewAddOptions.ADD_VENUE_BRANCH,
        show: true,
      });
    }
  };

  const handleActivateDeactivateUser = async (
    id: string | string[],
    isActive: boolean,
  ) => {
    try {
      console.log(`[ENTRY][ActivateDeActivateUser]`);
      const activateDeActivateUserResponse: UpdateUserResponse | undefined =
        await activateDeactivateUser(id, isActive);

      console.log(
        `[ActivateDeActivateUser][Close loading and receive success toast message].......`,
      );
      return activateDeActivateUserResponse;
    } catch (error: unknown) {
      console.warn(`[ActivateDeActivateUser]`, { error });
      throw error;
    }
  };

  const handleUserActivationStatus = async (
    userId: string,
    setToActive: boolean,
    statusValue: boolean,
  ): Promise<StatusResponse | undefined> => {
    try {
      console.log(`[ENTRY][ActivateDeActivateUser]`);

      const activeDeActiveRowResponse: UpdateUserResponse | undefined =
        await handleActivateDeactivateUser(userId, setToActive);

      const activateDeActivateUserResponse: StatusResponse | undefined =
        activeDeActiveRowResponse
          ? {
              statusCode: apiStatusCode.SUCCESS,
              data: activeDeActiveRowResponse,
            }
          : undefined;

      if (
        activateDeActivateUserResponse?.statusCode === apiStatusCode.SUCCESS
      ) {
        updateUserLocally(userId, setToActive);
        setShowActivateDeActivateSuccessToast(statusValue);
      }

      console.log(
        `[ActivateDeActivateUser][Close loading and receive success toast message].......`,
      );

      return activateDeActivateUserResponse;
    } catch (error: unknown) {
      console.warn(`[ActivateDeActivateUser]`, { error });
      throw error;
    }
  };

  const handleKebabAction = async (
    action: string,
    row: GetUsersResponse,
    rowIndex: number,
  ) => {
    setActiveRowIndex(rowIndex);
    if (action === KebabOption.ResendInvitation && row.email) {
      setUserDetailSummary(row);
      setUserOperation(UserOperation.EDIT);
      setSelectedUserIndex(rowIndex);
      setShowUserModal(true);
    }
    if (action === KebabOption.GenerateNewPin) {
      setUserDetailSummary(row);
      setUserOperation(UserOperation.EDIT);
      setSelectedUserIndex(rowIndex);
      setShowUserModal(true);
    }
    if (action === KebabOption.InActivate) {
      setCurrentActivateAction(KebabOption.InActivate);
      try {
        console.log(`[ENTRY][ActivateDeActivateUser]`);
        await handleUserActivationStatus(row?.id, false, true);
        if (userDetailSummary?.id === row.id) {
          setUserDetailSummary({
            ...userDetailSummary!,
            status: UserStatus.INACTIVE,
          });
        }

        console.log(
          `[ActivateDeActivateUser][Close loading and receive success toast message].......`,
        );
      } catch (error: unknown) {
        console.warn(`[ActivateDeActivateUser]`, { error });
        throw error;
      }
    }
    if (action === KebabOption.Activate) {
      setCurrentActivateAction(KebabOption.Activate);
      try {
        console.log(`[ENTRY][ActivateDeActivateUser]`);
        await handleUserActivationStatus(row?.id, true, true);
        if (userDetailSummary?.id === row.id) {
          setUserDetailSummary({
            ...userDetailSummary!,
            status: UserStatus.ACTIVE,
          });
        }

        console.log(
          `[ActivateDeActivateUser][Close loading and receive success toast message].......`,
        );
      } catch (error: unknown) {
        console.warn(`[ActivateDeActivateUser]`, { error });
      }
    }
    if (action === KebabOption.Delete) {
      setCurrentActivateAction(KebabOption.Delete);
      try {
        console.log(`[ENTRY][DeleteUser]`);

        const deleteUserRowResponse: UpdateUserResponse | undefined =
          await deleteUser(row?.id);

        const deleteUserResponse: StatusResponse | undefined =
          deleteUserRowResponse
            ? {
                statusCode: apiStatusCode.SUCCESS,
                data: deleteUserRowResponse,
              }
            : undefined;
        console.log(`[DeleteUser][Response]...`);
        if (deleteUserResponse?.statusCode === apiStatusCode.SUCCESS) {
          deleteUserLocally(row?.id);
          setShowSuccessDelete(true);
          setShowUserModal(false);
          setSelectedUsers([]);
        }
        console.log(
          `[DeleteUser][Close loading and receive success toast message].......`,
        );
      } catch (error: unknown) {
        console.warn(`[DeleteUser]`, { error });
        throw error;
      } finally {
        console.warn(`[DeleteUser] finally block...`);
      }
    }
    if (action === KebabOption.Edit) {
      setUserDetailSummary(row);
      setUserOperation(UserOperation.EDIT);
      setSelectedUserIndex(rowIndex);
      setShowUserModal(true);
    }

    setActiveRowIndex(null);
  };

  const loadingActionMenu = getLoadingActionMenu(
    activeRowIndex,
    activateDeactivateLoading,
    deleteUserLoading,
    currentActivateAction,
  );

  const handleSwitchChange = async () => {
    if (!activeLabel) {
      try {
        console.log(`[ENTRY][ActivateDeActivateUser]`);
        if (userDetailSummary?.id) {
          await handleUserActivationStatus(userDetailSummary?.id, true, false);
          setUserDetailSummary({
            ...userDetailSummary!,
            status: UserStatus.ACTIVE,
          });
        }
        console.log(
          `[ActivateDeActivateUser][Close loading and receive success toast message].......`,
        );
        setActiveLabel(true);
      } catch (error: unknown) {
        console.warn(`[ActivateDeActivateUser]`, { error });
        throw error;
      }
    } else {
      try {
        console.log(`[ENTRY][ActivateDeActivateUser]`);
        if (userDetailSummary?.id) {
          await handleUserActivationStatus(
            userDetailSummary?.id ?? '',
            false,
            false,
          );
          setUserDetailSummary({
            ...userDetailSummary!,
            status: UserStatus.INACTIVE,
          });
        }

        console.log(
          `[ActivateDeActivateUser][Close loading and receive success toast message].......`,
        );
        setActiveLabel(false);
      } catch (error: unknown) {
        console.warn(`[ActivateDeActivateUser]`, { error });
        throw error;
      }
    }
  };

  const handleConfirm = () => {
    setShowStatusConfirmationModal(false);
    if (currentActivateAction === KebabOption.Activate) {
      handleBulkActivateDeactivate(true);
    } else if (currentActivateAction === KebabOption.InActivate) {
      handleBulkActivateDeactivate(false);
    } else if (currentActivateAction === KebabOption.Delete) {
      handleBulkDelete();
    }
  };

  const handleEditUser = () => {
    console.log(`[ENTRY][HandleEditUser]`);
    setShowUserModal(true);
    setUserOperation(UserOperation.EDIT);
  };

  const handleSettingMenuSelect = (label: string | undefined) => {
    if (!label) return;
    if (label === 'userManagement.settingOption.editVenue') {
      setOpenEditVenueModal(true);
    } else if (label === 'userManagement.settingOption.download') {
      toast.info(<Trans i18nKey="common.functionalityComingSoon" />, {
        autoClose: 3000,
      });
    } else if (label === 'userManagement.settingOption.viewUniversalStaff') {
      setShowUniversalStaffLoginModal(true);
    } else if (label === 'userManagement.settingOption.editUniversalStaff') {
      setShowEditUniversalStaffLoginModal(true);
    } else if (label === 'userManagement.settingOption.createVenueBranch') {
      setAddOrg({
        type: VenuesViewAddOptions.ADD_VENUE_BRANCH,
        show: true,
      });
    }
  };

  const handleSelectChildOrganization = (
    selectedChildItem: OrganizationUnitChildItem,
  ) => {
    setSelectedChildOrgnizationLabel(selectedChildItem);
  };

  const userText: string = selectedUsers?.length === 1 ? 'user' : 'user(s)';

  const showUsersFromURL: boolean =
    (selectedOrganisationToggle === OrganisationToggleEnum.VENUES ||
      selectedOrganisationToggle === OrganisationToggleEnum.ORGANISATION) &&
    searchParams.get(VenuesViewSearchParams.SHOW_USERS) === 'true';

  return (
    <div className="header-content">
      {openEditVenueModal && (
        <EditVenueBranch
          isOpen={openEditVenueModal}
          setIsOpen={setOpenEditVenueModal}
        />
      )}
      {/* Renders Table content. */}
      {showActivateDeActivateSuccessToast && (
        <SuccessToast
          message={
            currentActivateAction === KebabOption.Activate
              ? SuccessMessage.ACTIVATE_SUCCESSFULLY
              : SuccessMessage.DEACTIVATE_SUCCESSFULLY
          }
          onClose={() => setShowActivateDeActivateSuccessToast(false)}
          className="bottom-15 bg-white right-10"
        />
      )}
      {showDeleteSuccessToast && (
        <SuccessToast
          message={SuccessMessage.DELETE_SUCCESSFULLY}
          onClose={() => setShowSuccessDelete(false)}
          className="bottom-15 bg-white right-10"
        />
      )}

      <div
        className="table-content pt-6 flex flex-col overflow-hidden"
        style={{ boxShadow: '0px 0px 25px #2F41A51F' }}
      >
        {(selectedOrganisationToggle === OrganisationToggleEnum.VENUES ||
          selectedOrganisationToggle === OrganisationToggleEnum.ORGANISATION) &&
        !showUsersFromURL ? (
          <VenuesView
            setSelectedOrganisationToggle={setSelectedOrganisationToggle}
            selectedOrganisationToggle={selectedOrganisationToggle}
            handleSearchChange={handleUserSearchChange}
            query={userSearchQuery}
            usersList={usersSummary?.data ?? []}
            handleAddUser={handleAddUser}
          />
        ) : (
          <>
            {/* Header */}
            <UserManagementTableHeader
              allUsers={usersSummary?.data ?? []}
              selectedOrganisationToggle={selectedOrganisationToggle}
              setSelectedOrganisationToggle={setSelectedOrganisationToggle}
              handleAddActions={handleAddUser}
              addMenuOptions={addMenuOptionsforUserManagement}
              searchQuery={userSearchQuery}
              handleOnChangeSearch={handleUserSearchChange}
              selectedCount={selectedUsers.length}
              unitTranslationKey={
                selectedUsers.length === 1
                  ? 'userManagement.table.user'
                  : 'userManagement.table.users'
              }
              handleClose={handleClose}
              handleActivate={handleActivate}
              handleDeactivate={handleDeactivate}
              handleDelete={handleDelete}
              actionVisibility={actionVisibility}
              onSettingMenuSelect={handleSettingMenuSelect}
              selectedLabel={selectedChildOrganizationLabel}
              handleSelect={handleSelectChildOrganization}
            >
              <UserManagementFilter
                filters={filters}
                setFilters={setFilters}
                setFilterModalOpen={setIsFilterModalOpen}
                isOpenUserModal={isFilterModalOpen}
              />
            </UserManagementTableHeader>

            {/* Scrollable Table */}
            <div className="flex-grow">
              <Table
                onSelectionChange={handleSelectionChange}
                tableHeader={getUserTableHeader()}
                selectedRows={selectedUsers}
                tableData={filteredUsers}
                loading={userSummaryLoading || bulkActionLoading}
                onViewDetail={handleViewUser}
                onKebabSelect={handleKebabAction}
                loadingMenu={loadingActionMenu ?? { rowIndex: -1, action: '' }}
                getKebabMenuOptions={getUserKebabOptions}
                onShouldCloseKebabMenu={(action) => {
                  return [
                    KebabOption.Edit,
                    KebabOption.ResendInvitation,
                    KebabOption.GenerateNewPin,
                  ].includes(action as KebabOption);
                }}
              />
            </div>

            {/* Sticky Footer */}
            <TableFooter
              footerText={'userManagement.table.footer'}
              displayedCount={selectedUsers?.length || filteredUsers?.length}
              totalCount={usersSummary?.data.length}
              unitText="userManagement.table.users"
            />
          </>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          isOpenUserModal={showUserModal}
          operation={userOperation}
          handleCloseUserModal={() => setShowUserModal(false)}
          onCancel={() => setShowUserModal(false)}
          handleEditUser={handleEditUser}
          viewUserRowIndex={selectedUserIndex ?? 0}
          selectedUser={userDetailSummary}
          onKebabSelect={handleKebabAction}
          loadingMenu={loadingActionMenu}
          activeLabel={activeLabel}
          setActiveLabel={setActiveLabel}
          onChangeSwitch={handleSwitchChange}
          activeDeactiveLoading={activateDeactivateLoading}
        />
      )}
      {/* Modal that tells the client they cannot activate/deactivate an invited user */}
      {showInvitedModal && (
        <ConfirmationModal
          isOpen={showInvitedModal}
          onClose={() => setShowInvitedModal(false)}
          message={
            <Trans
              i18nKey="userManagement.invitedMessage"
              values={{
                userText,
                confirmActionText: confirmationModalText,
              }}
            />
          }
        />
      )}
      {/* Modal that tells the client confirmation for  api call */}
      {showStatusConfirmationModal && (
        <ConfirmationModal
          showConfirmButton
          isOpen={showStatusConfirmationModal}
          onConfirm={handleConfirm}
          onClose={() => setShowStatusConfirmationModal(false)}
          message={
            <Trans
              i18nKey={
                currentActivateAction === KebabOption.Delete
                  ? 'userManagement.deleteConfirmMessage'
                  : 'userManagement.confirmMessage'
              }
              values={{
                userText,
                confirmActionText: confirmationModalText,
              }}
              components={{ bold: <strong /> }}
            />
          }
        />
      )}
      {showUniversalStaffLoginModal && (
        <ViewUniversalStaffLoginModal
          onClose={() => setShowUniversalStaffLoginModal(false)}
        />
      )}
      {showEditUniversalStaffLoginModal && selectedChildOrganizationLabel && (
        <EditUniversalStaffLoginModal
          setSelectedBranch={setSelectedChildOrgnizationLabel}
          selectedBranch={selectedChildOrganizationLabel}
          onClose={() => setShowEditUniversalStaffLoginModal(false)}
          setShowChangePasswordSuccessToast={setShowChangePasswordSuccessToast}
        />
      )}
      {(showChangePasswordSuccessToast || showVenueBranchCreatedToast) && (
        <SuccessToast
          message={
            showChangePasswordSuccessToast
              ? SuccessMessage.UNIVERSAL_STAFF_LOGIN_PASSWORD_CHANGED_SUCCESSFULLY
              : SuccessMessage.VENUE_BRANCH_CREATED_SUCCESSFULLY
          }
          onClose={() => {
            setShowChangePasswordSuccessToast(false);
            setShowVenueBranchCreatedToast(false);
          }}
          className="bottom-15 bg-white right-10"
        />
      )}
      {addOrg.show && addOrg.type && (
        <AddOrganisationModal
          show={addOrg.show}
          onToggle={(value) => setAddOrg({ show: value, type: undefined })}
          type={addOrg.type}
          onSuccess={() => setShowVenueBranchCreatedToast(true)}
        />
      )}
    </div>
  );
};

export default UserManagement;
